import React, { useContext, useEffect, useMemo, useState } from "react";
import { PreferencesContext } from "../../../../PreferenceContext";
import { PAGE_SIZE } from "../../../../services/common/const";
import {
  GetInvestigationParameterList,
  GetInvestigationParameterRangeList,
  GetPatientReportParameterList,
  SavePatientReportParameter,
  UpdatePatientReportParameter,
} from "../../../../services/ReportParameterService";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import SkeletonWrapper from "../../../../components/Skeleton/SkeletonWrapper";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { CustomTextField } from "../../../../components/CustomFields/TextField";
import { LoadingButton } from "@mui/lab";
import { AlertType } from "../../../../services/models/AlertType";
import dayjs from "dayjs";
import CustomDateTimePicker from "../../../../components/TimePicker/CustomDateTimePicker";
import { Edit } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseIcon from '@mui/icons-material/Close';
import ReportParameterForRange from "./ReportParameterForRange";

// SCHEMA FOR RESULTDATA
interface ResultData {
  patientReportParameterColl: Array<any>;
  unitColl: Array<any>;
  totalCount: number;
}
// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  patientReportParameterColl: [],
  unitColl: [],
  totalCount: 0,
};

// SCHEMA FOR PARAMETER
interface Parameter {
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}
// SCHEMA
interface Data {
  reportParameterId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  isDeleted: boolean;
}
// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

interface IReportParameter {
  memberPatientReportParameterValueId: number;
  displayOrder: number;
  investigationId: number;
  investigationName: string;
  investigationParameterId: number;
  parameterName: string;
  investigationParameterUnitId: number;
  unitName: string;
  unitValue: string;
  optionalUnit?: string;
  specimen?: string;
  remarks?: string;
  rangeValue?: string;
  entryDateTime?: string | null;
  isDeleted?: boolean;
  mode?: string;
}
interface IReportParameterRange {
  memberPatientReportParameterForRangeValueId?: number;
  memberPatientReportParameterValueId?: number;
  opdId?: number;
  patientId?: number;
  investigationId: number;
  investigationParameterId: number;
  optionValue: string;
  displayOrder: number;
  isDeleted?: boolean;
  mode?: string;
}
interface formInitialValueTypes {
  memberPatientReportParameterId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  opdId: number;
  patientId: number;
  memberId: number;
  entryDateTime: string | null;
  reportParameterColl: Array<IReportParameter>;
  reportParameterRangeColl: Array<IReportParameterRange>;
}

const PatientReportParameter = (Props: any) => {
  // PROPS
  const { isForPopUp = false, handleClosePopUp, opd, patientId } = Props;

  const navigate = useNavigate();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [param, setParam] = useState(parameters);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
  const [unitColl, setUnitColl] = useState<any[]>([]);
  const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [reportParameterData, setReportParameterData] = useState<any[]>([]);
  const [openParameterDialog, setOpenParameterDialog] =
    useState<boolean>(false);
  const [reportParameterList, setReportParameterList] = useState<any[]>([]);
  const [dialogSelected, setDialogSelected] = useState<readonly number[]>([]);
  const [counData, setCounData] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [dialogPage, setDialogPage] = useState(1);
  const [dialogSearch, setDialogSearch] = useState("");
  const [dialogValue, setDialogValue] = useState("");
  const [parameterData, setParameterData] = useState<Record<number, any>>({});
  const [investigationParameterRangeColl, setInvestigationParameterRangeColl] =
    useState<any[]>([]);
  const [quickRangeSelected, setQuickRangeSelected] = useState<
    Record<number, string>
  >({});
  const [selectedRangeRow, setSelectedRangeRow] = useState<any>(null);
  const [openRangeDialog, setOpenRangeDialog] = useState<boolean>(false);
  const [quickRangeRow, setQuickRangeRow] = useState<any>(null);
  const [openQuickRangeDialog, setOpenQuickRangeDialog] =
    useState<boolean>(false);
  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
  });

  const initReportParameterData: formInitialValueTypes = {
    memberPatientReportParameterId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    opdId: 0,
    patientId: 0,
    memberId: 0,
    entryDateTime: new Date().toISOString(),
    reportParameterColl: [],
    reportParameterRangeColl: [],
  };

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTING PARAMS
  const urlParams = useQuery();
  const page = urlParams.get("page");
  const curPage = page ? Number(page) || 1 : 1;

  const [value, setValue] = useState("");
  const [mainSearch, setMainSearch] = useState("");
  const filteredReportParameterRows = useMemo(() => {
    const searchText = mainSearch.trim().toLocaleLowerCase();
    return reportParameterData
      .map((item: any, index: number) => ({ item, index }))
      .filter(({ item }: any) => {
        if (!searchText) return true;
        return [item?.investigationName, item?.parameterName, item?.specimen, item?.unitName, item?.remarks, item?.rangeValue]
          .some((field) => field?.toString().toLocaleLowerCase().includes(searchText));
      });
  }, [reportParameterData, mainSearch]);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    entryDateTime: Yup.string().nullable(),
    reportParameterColl: Yup.array().of(
      Yup.object().shape({
        displayOrder: Yup.number().nullable(),
        investigationId: Yup.number().nullable(),
        investigationParameterId: Yup.number().nullable(),
        parameterName: Yup.string().nullable(),
        investigationParameterUnitId: Yup.number().nullable(),
        unitName: Yup.string().nullable(),
        unitValue: Yup.string().nullable(),
        rangeValue: Yup.string().nullable(),
        optionValue: Yup.string().nullable(),
      }),
    ),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initReportParameterData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "14.66%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="left"
          >
            Investigation
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "14.66%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="left"
          >
            Parameter
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "14.66%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="left"
          >
            Specimen
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "26.83%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="center"
          >
            Value
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "14.58%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="left"
          >
            Range
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "14.58%",
              display: {
                xs: "none",
                sm: "table-cell",
              },
            }}
            align="left"
          >
            Remarks
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // APPLY MAIN LIST SEARCH WITHOUT CHANGING THE URL
  function searchValue(val: string) {
    setMainSearch(val.trim());
  }

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = reportParameterData?.map(
        (n: any) => n?.investigationParameterId || 0,
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number,
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number],
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const handleManagePopup = async (code: string, data?: any) => {
    setOpenRangeDialog(false);
    setSelectedRangeRow(null);
    if (code === "REPORT_PARAMETER") {
      await fetchInvestigationReportParameterList(
        reportParameterList,
        dialogPage,
        dialogSearch,
      );
      await getInvestigationParameterRangeList("", true);
    }
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  const handleOpenAddPopup = async () => {
    const currentIds =
      reportParameterData?.map((item: any) => item?.investigationParameterId) ||
      [];
    setDialogSelected(currentIds);
    const existingRangeSelections: Record<number, string> = {};
    reportParameterData?.forEach((item: any) => {
      if (item?.investigationParameterId && item?.rangeValue) {
        existingRangeSelections[item.investigationParameterId] =
          item.rangeValue;
      }
    });
    setQuickRangeSelected(existingRangeSelections);
    setDialogSearch("");
    setDialogPage(1);
    setOpenParameterDialog(true);
    await fetchInvestigationReportParameterList([], 1, "");
  };

  const handleDialogSearch = async (searchText: string) => {
    const trimmedSearch = searchText.trim();
    setDialogSearch(trimmedSearch);
    setDialogPage(1);
    await fetchInvestigationReportParameterList([], 1, trimmedSearch);
  };

  // ON CHANGE PAGE
  const handleDialogPageChange = async (
    event: React.ChangeEvent<unknown>,
    pageNumber: number,
  ) => {
    setDialogPage(pageNumber);
    await fetchInvestigationReportParameterList([], pageNumber, dialogSearch);
  };

  // GET INVESTIGATION PARAMETER LIST
  async function fetchInvestigationReportParameterList(
    existingColl: any[] = [],
    pageIndex: number = dialogPage,
    searchText: string = "",
  ) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchText);
      params.append("mode", "LIST".toString());
      const result: any = await GetInvestigationParameterList(params);
      const rawColl = result?.data?.data?.investigationParameterColl ?? [];
      const resultData = result?.data?.data;
      const dataCount = resultData?.totalCount || 0;
      setTotalCount(dataCount);
      const showDataCount = Math.ceil(dataCount / param.pageSize);
      setCounData(showDataCount);
      const startIndex =
        dataCount > 0 ? (pageIndex - 1) * param.pageSize + 1 : 0;
      const endIndex = (pageIndex - 1) * param.pageSize + rawColl?.length || 0;
      setPageStartIndex(startIndex);
      setPageEndIndex(endIndex);

      const passedExisting = new Map(
        (existingColl || []).map((d: any) => [d.investigationParameterId, d]),
      );

      const finalColl = rawColl.map((item: any) => {
        const data =
          parameterData[item.investigationParameterId] ||
          passedExisting.get(item.investigationParameterId);
        return {
          ...item,
          reportParameterDetailId: data?.reportParameterDetailId || 0,
        };
      });
      setReportParameterList(finalColl);
      setParameterData((prev) => {
        const updated = { ...prev };
        finalColl?.forEach((item: any) => {
          updated[item.investigationParameterId] = item;
        });
        return updated;
      });
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  }

  const handleAddSelectedParameters = () => {
    const existingMap = new Map(
      (reportParameterData || []).map((item: any) => [
        item.investigationParameterId,
        item,
      ]),
    );
    const updatedList = dialogSelected
      .map((id) => existingMap?.get(id) || parameterData[id])
      .filter(Boolean)
      .map((item: any) => {
        const existing = existingMap?.get(item.investigationParameterId);
        const selectedRange = quickRangeSelected[item.investigationParameterId];
        const base = existing || {
          memberPatientReportParameterValueId:
            item?.memberPatientReportParameterValueId || 0,
          memberPatientReportParameterId: 0,
          investigationId: item.investigationId,
          investigationName: item.investigationName,
          investigationParameterId: item.investigationParameterId,
          parameterName: item.parameterName,
          parameterType: item.parameterType || "",
          investigationParameterUnitId: item.investigationParameterUnitId || 0,
          unitName: item.unitName || "",
          optionalUnit: item.optionalUnit || "",
          unitValue: "",
          specimen: item?.specimen || "",
          remarks: item?.remarks || "",
          rangeValue: item?.rangeValue || "",
          optionValue: "",
          isDeleted: false,
          mode: "I",
          addedInSession: true,
        };
        return selectedRange ? { ...base, rangeValue: selectedRange } : base;
      });

    setUnitColl((prev) => {
      const merged = [...prev];
      updatedList.forEach((item: any) => {
        const investigationParameterId = item.investigationParameterId;

        const existingIndex = merged.findIndex(
          (u: any) => u.investigationParameterId === investigationParameterId,
        );

        if (existingIndex >= 0) {
          merged[existingIndex] = {
            ...merged[existingIndex],
            unitName: merged[existingIndex].unitName || item.unitName || "",
            optionalUnit:
              merged[existingIndex].optionalUnit || item.optionalUnit || "",
            investigationParameterUnitId:
              merged[existingIndex].investigationParameterUnitId ||
              item.investigationParameterUnitId ||
              0,
          };
        } else {
          merged.push({
            investigationParameterId,
            investigationParameterUnitId:
              item.investigationParameterUnitId || 0,
            unitName: item.unitName || "",
            optionalUnit: item.optionalUnit || "",
          });
        }
      });
      return merged;
    });

    setReportParameterData(updatedList);

    methods.setValue(
      "reportParameterColl",
      updatedList.map((item: any, index: number) => ({
        memberPatientReportParameterValueId:
          item?.memberPatientReportParameterValueId || 0,
        memberPatientReportParameterId:
          item?.memberPatientReportParameterId || 0,
        displayOrder: index,
        investigationId: item?.investigationId,
        investigationName: item?.investigationName,
        investigationParameterId: item?.investigationParameterId,
        parameterName: item?.parameterName,
        investigationParameterUnitId: item?.investigationParameterUnitId || 0,
        unitName: item?.unitName || "",
        optionalUnit: item?.optionalUnit || "",
        unitValue: item?.unitValue || "",
        specimen: item?.specimen || "",
        remarks: item?.remarks || "",
        rangeValue: item?.rangeValue || "",
        optionValue: item?.optionValue || "",
        isDeleted: item?.isDeleted || false,
        mode:
          item?.memberPatientReportParameterValueId > 0 ? "U" : "I",
        addedInSession: item?.addedInSession || false,
      })),
      { shouldValidate: true, shouldDirty: true },
    );
    setQuickRangeSelected({});
    setOpenParameterDialog(false);
    getInvestigationParameterRangeList("", true);
  };

  const getReportParameterList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("patientId", patientId);
      params.append("opdId", opd?.opdId?.toString() || 0);
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", curPage?.toString());
      params.append("pageSize", "5000");
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", mainSearch);
      params.append("mode", "LIST");
      const result: any = await GetPatientReportParameterList(params);
      if (result?.data?.data?.patientReportParameterColl) {
        const resultData: ResultData = result?.data?.data;
        let patientReportParameterColl =
          resultData?.patientReportParameterColl || [];
        setResultData(resultData);

        const unitOptions = (resultData?.unitColl || [])?.flatMap(
          (item: any) => {
            const options: any[] = [];
            if (item?.unitName?.trim()) {
              options.push({
                investigationParameterId: item.investigationParameterId,
                investigationParameterUnitId:
                  item.investigationParameterUnitId ?? 0,
                unitName: item.unitName,
                isOptional: false,
              });
            }
            if (item?.optionalUnit?.trim()) {
              options.push({
                investigationParameterId: item.investigationParameterId,
                investigationParameterUnitId:
                  item.investigationParameterUnitId ?? 0,
                unitName: item.optionalUnit,
                isOptional: true,
              });
            }
            return options;
          },
        );
        setUnitColl(unitOptions);
        const reportParameterDetailColl = patientReportParameterColl?.map(
          (item: any) => {
            const defaultUnit =
              item?.unitName?.trim() ||
              unitOptions?.find(
                (u: any) =>
                  u?.investigationParameterId ===
                  item?.investigationParameterId && !u?.isOptional,
              )?.unitName ||
              "";
            return {
              memberPatientReportParameterValueId:
                item?.memberPatientReportParameterValueId ?? 0,
              memberPatientReportParameterId:
                item?.memberPatientReportParameterId ?? 0,
              reportParameterId: item?.reportParameterId ?? 0,
              reportParameterDetailId: item?.reportParameterDetailId ?? 0,
              consumerId: item?.consumerId ?? 0,
              orgId: item?.orgId ?? 0,
              branchId: item?.branchId ?? 0,
              investigationId: item?.investigationId ?? 0,
              investigationName: item?.investigationName ?? "",
              investigationParameterId: item?.investigationParameterId ?? 0,
              parameterName: item?.parameterName ?? "",
              parameterType: item?.parameterType ?? "",
              investigationParameterUnitId:
                item?.investigationParameterUnitId || 0,
              unitName: defaultUnit,
              unitValue: item?.unitValue ?? "",
              specimen: item?.specimen ?? "",
              remarks: item?.remarks ?? "",
              rangeValue: item?.rangeValue ?? "",
              optionValue: item?.optionValue ?? "",
              displayOrder: item?.displayOrder ?? 0,
              isDeleted: item?.isDeleted ?? false,
              mode: item?.memberPatientReportParameterId > 0 ? "U" : "I",
            };
          },
        );
        const existingMemberPatientReportParameterId =
          patientReportParameterColl?.find(
            (item: any) => item?.memberPatientReportParameterId > 0,
          )?.memberPatientReportParameterId || 0;

        methods.setValue(
          "memberPatientReportParameterId",
          existingMemberPatientReportParameterId,
          { shouldValidate: false },
        );
        methods.setValue("reportParameterColl", reportParameterDetailColl, {
          shouldValidate: false,
        });
        setReportParameterData(reportParameterDetailColl);
      }
      setShowSkeleton(false);
      setIsLoading(false);
    } catch (e) {
      setShowSkeleton(false);
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  const hasExistingReportParameter = Boolean(
    reportParameterData?.length > 0 &&
    reportParameterData?.some(
      (item: any) => item?.memberPatientReportParameterId > 0,
    ),
  );

  const savePatientReportParameter = async (value: formInitialValueTypes) => {
    try {
      const reportParameterColl =
        value?.reportParameterColl
          ?.filter((item: any) => {
            if (item?.isDeleted) return false;
            const hasValue =
              (item?.unitValue && item.unitValue.toString().trim() !== "") ||
              (item?.optionValue && item.optionValue.toString().trim() !== "") ||
              (item?.rangeValue && item.rangeValue.toString().trim() !== "") ||
              (item?.remarks && item.remarks.toString().trim() !== "");
            const isExisting = item?.memberPatientReportParameterValueId > 0;
            return hasValue || item?.addedInSession || isExisting;
          })
          ?.map((item: any, index: number) => {
            const defaultUnit = unitColl.find(
              (x: any) =>
                x.investigationParameterId === item.investigationParameterId,
            );

            return {
              memberPatientReportParameterValueId:
                item?.memberPatientReportParameterValueId || 0,
              investigationParameterId: item?.investigationParameterId,
              parameterName: item?.parameterName,
              investigationParameterUnitId:
                item?.investigationParameterUnitId ||
                defaultUnit?.investigationParameterUnitId ||
                0,
              unitName: item?.unitName || defaultUnit?.unitName || "",
              unitValue: item?.unitValue || "",
              investigationId: item?.investigationId || 0,
              investigationName: item?.investigationName || "",
              specimen: item?.specimen || "",
              remarks: item?.remarks || "",
              rangeValue: item?.rangeValue || "",
              optionValue: item?.optionValue || "",
              entryDateTime: value?.entryDateTime
                ? dayjs(value.entryDateTime).format("YYYY-MM-DD HH:mm:ss")
                : null,
              displayOrder: index + 1,
              isDeleted: item?.isDeleted || false,
              mode: item?.mode || "I",
            };
          }) || [];

      let result: any = "";

      const formData = {
        memberPatientReportParameterId:
          value?.memberPatientReportParameterId ||
          opd?.memberPatientReportParameterId ||
          0,
        consumerId: userContext?.consumerId,
        orgId: userContext?.organizationId,
        branchId: userContext?.branchId,
        opdId: opd?.opdId || 0,
        patientId: patientId,
        memberId: 0,
        entryDateTime: value?.entryDateTime,
        parameterName:
          reportParameterColl
            ?.map((item: any) => item?.parameterName)
            .join(", ") || "",
        reportParameterJson: JSON.stringify(reportParameterColl),
        rowManagedBy: userContext?.loginDetail?.userId,
      };
      if (hasExistingReportParameter) {
        result = await UpdatePatientReportParameter(formData);
      } else {
        result = await SavePatientReportParameter(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          handleClosePopUp("REPORT_PARAMETER", result.data);
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  const handleOptionValueChange = (
    index: number,
    item: any,
    optionValue: string,
  ) => {
    methods?.setValue(`reportParameterColl.${index}.optionValue`, optionValue, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const currentRanges = methods?.getValues("reportParameterRangeColl") || [];
    const existingIndex = currentRanges?.findIndex(
      (x: any) => x.investigationParameterId === item.investigationParameterId,
    );
    const rangeData = {
      memberPatientReportParameterForRangeValueId:
        existingIndex >= 0
          ? currentRanges[existingIndex]
            ?.memberPatientReportParameterForRangeValueId || 0
          : 0,
      memberPatientReportParameterValueId:
        item?.memberPatientReportParameterValueId || 0,
      opdId: opd?.opdId || 0,
      patientId: opd?.patientId || 0,
      investigationId: item?.investigationId || 0,
      investigationParameterId: item?.investigationParameterId || 0,
      optionValue,
      displayOrder:
        existingIndex >= 0
          ? currentRanges[existingIndex]?.displayOrder
          : currentRanges.length + 1,
      isDeleted: false,
      mode: existingIndex >= 0 ? "U" : "I",
    };
    if (existingIndex >= 0) {
      currentRanges[existingIndex] = rangeData;
    } else {
      currentRanges.push(rangeData);
    }
    methods.setValue("reportParameterRangeColl", currentRanges, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const getInvestigationParameterRangeList = async (
    searchValue: string,
    oninit: boolean,
  ) => {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", "5000");
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "LIST");

      const result: any = await GetInvestigationParameterRangeList(params);
      let rangeColl: any[] = [];
      if (result?.data?.data?.rangeColl) {
        rangeColl = result?.data?.data?.rangeColl;
        if (oninit) setInvestigationParameterRangeColl(rangeColl);
      }
      return rangeColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  const getOptionValues = (optionValue: any) => {
    if (!optionValue) return [];
    if (Array.isArray(optionValue)) {
      return optionValue;
    }
    if (typeof optionValue !== "string") {
      return [];
    }
    try {
      const cleanJson = optionValue
        .replace(/,\s*}/g, "}")
        .replace(/,\s*]/g, "]");
      const parsed = JSON.parse(cleanJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.log("optionValue parse error:", optionValue);
      return [];
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    setIsLoading(true);
    await savePatientReportParameter(data);
    setIsLoading(false);
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await getReportParameterList();
      await fetchInvestigationReportParameterList([], 1, dialogSearch);
      await getInvestigationParameterRangeList("", true);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [param, curPage, mainSearch]);

  return (
    <>
      <Box className={"main-container"}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <SkeletonWrapper
              isLoading={isLoading}
              className="skeleton-search-box"
            >
              <TextField
                id="searchBox"
                placeholder="Search..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchValue(value);
                  }
                }}
                type="text"
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 235,
                }}
                onChange={(e) => setValue(e.target.value)}
                value={value}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        onClick={() => {
                          searchValue(value);
                        }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: value && (
                    <IconButton
                      aria-label="clear search"
                      onClick={() => {
                        searchValue("");
                        setValue("");
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  ),
                }}
              />
            </SkeletonWrapper>

            <FormControl>
              <SkeletonWrapper isLoading={isLoading} className="skeleton-input">
                <Controller
                  control={methods.control}
                  name="entryDateTime"
                  render={({ field }) => (
                    <CustomDateTimePicker
                      value={field.value ? dayjs(field.value) : null}
                      disabled={false}
                      minDate={dayjs()}
                      onTimeChange={(newDateTime) => {
                        field.onChange(
                          newDateTime ? newDateTime.toDate() : null,
                        );
                      }}
                      error={!!methods.formState.errors.entryDateTime}
                    />
                  )}
                />
              </SkeletonWrapper>
            </FormControl>
          </Box>
        </Grid>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container spacing={2}>
              <Grid item xs={12} mt={1}>
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: "550px",
                    overflowY: "auto",
                  }}
                >
                  <Table size="small">
                    <EnhancedTableHead
                      numSelected={filteredReportParameterRows.length}
                      order={order}
                      orderBy={orderBy}
                      onSelectAllClick={handleCheckSelectAll}
                      rowCount={filteredReportParameterRows.length}
                    />
                    <TableBody>
                      {showSkeleton ? (
                        Array(skeletonRowCount)
                          ?.fill(0)
                          ?.map((skeletonRow, index) => {
                            return (
                              <TableRow
                                key={"skeleton-row" + index?.toString()}
                              >
                                <TableCell colSpan={6}>
                                  <SkeletonWrapper
                                    isLoading={showSkeleton}
                                    className="skeleton-input"
                                  >
                                    <span></span>
                                  </SkeletonWrapper>
                                </TableCell>
                              </TableRow>
                            );
                          })
                      ) : (
                        <>
                          {stableSort(filteredReportParameterRows, (a: any, b: any) => getComparator(order, orderBy)(a.item, b.item)).map(({ item, index }: any) => {
                            return (
                              <TableRow key={index}>
                                <TableCell colSpan={6} sx={{ p: 1 }}>
                                  <Grid
                                    container
                                    spacing={2}
                                    alignItems="center"
                                  >
                                    <Grid item xs={12} sm={1.76} md={1.76}>
                                      <Typography
                                        sx={{
                                          display: { xs: "block", sm: "none" },
                                          fontWeight: 600,
                                        }}
                                      >
                                        Investigation
                                      </Typography>
                                      {item.investigationName}
                                    </Grid>
                                    <Grid item xs={12} sm={1.76} md={1.76}>
                                      <Typography
                                        sx={{
                                          display: { xs: "block", sm: "none" },
                                          fontWeight: 600,
                                        }}
                                      >
                                        Parameter
                                      </Typography>
                                      {item.parameterName}
                                    </Grid>
                                    <Grid item xs={12} sm={1.76} md={1.76}>
                                      <Typography
                                        sx={{
                                          display: { xs: "block", sm: "none" },
                                          fontWeight: 600,
                                        }}
                                      >
                                        Specimen
                                      </Typography>
                                      {item.specimen}
                                    </Grid>
                                    <Grid item xs={12} sm={3.22} md={3.22}>
                                      <Typography
                                        sx={{
                                          display: { xs: "block", sm: "none" },
                                          fontWeight: 600,
                                        }}
                                      >
                                        Value
                                      </Typography>
                                      {(() => {
                                        if (item?.parameterType === "Qualitative") {
                                          const rangeItems =
                                            investigationParameterRangeColl?.filter(
                                              (r: any) =>
                                                r?.investigationParameterId ===
                                                item?.investigationParameterId &&
                                                r?.optionValue
                                                  ?.toString()
                                                  .trim() !== "",
                                            );
                                          const selectedOptionValue =
                                            methods?.watch(
                                              `reportParameterColl.${index}.optionValue`,
                                            );
                                          return (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 0.5,
                                                alignItems: "center",
                                              }}>
                                              {rangeItems?.map(
                                                (
                                                  rangeItem: any,
                                                  rangeIndex: number,
                                                ) => {
                                                  const isSelected =
                                                    selectedOptionValue ===
                                                    rangeItem.optionValue;
                                                  return (
                                                    <Box
                                                      key={
                                                        rangeItem?.memberPatientReportParameterRangeValueId ||
                                                        `${item?.investigationParameterId}-${rangeIndex}`
                                                      }
                                                      className="quick-chip"
                                                      sx={{
                                                        padding:
                                                          "6px 10px !important",
                                                        ...(isSelected && {
                                                          "&&": {
                                                            backgroundColor:
                                                              "primary.main",
                                                            color: "#fff",
                                                            borderColor:
                                                              "primary.main",
                                                          },
                                                        }),
                                                      }}                                                      
                                                      onClick={() => {
                                                        handleOptionValueChange(
                                                          index,
                                                          item,
                                                          rangeItem.optionValue,
                                                        );
                                                      }}>
                                                      <span>{rangeItem.optionValue}</span>
                                                      <Tooltip title="Edit Option Value" placement="top">
                                                        <Button
                                                          size="small"
                                                          sx={{ 
                                                            minWidth: "20px",
                                                            width: "20px",
                                                            height: "20px",
                                                            padding: 0,
                                                            marginTop: "-3px",
                                                            marginLeft: "2px",
                                                          }}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedRangeRow({
                                                              ...item,
                                                              optionValue: rangeItem.optionValue,
                                                              memberPatientReportParameterForRangeValueId:
                                                                rangeItem?.memberPatientReportParameterRangeValueId || 0,
                                                            });
                                                            setOpenRangeDialog(true);
                                                          }}>
                                                          <Edit 
                                                            fontSize="inherit"
                                                            sx={{ 
                                                              width: "14px !important",
                                                              height: "14px !important",
                                                              color: isSelected ? "#fff" : "primary.main",
                                                            }} 
                                                          />
                                                        </Button>
                                                      </Tooltip>
                                                    </Box>
                                                  );
                                                },
                                              )}
                                              <Tooltip title="Add Option Value" placement="top">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => {
                                                    setSelectedRangeRow({
                                                      ...item,
                                                      optionValue: "",
                                                      memberPatientReportParameterForRangeValueId: 0,
                                                    });
                                                    setOpenRangeDialog(true);
                                                  }}>
                                                  <AddOutlinedIcon fontSize="small" className="text-link link-cursor" />
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          );
                                        }
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              border: "1px solid",
                                              borderColor: "divider",
                                              borderRadius: 1,
                                              overflow: "hidden",
                                              maxWidth: 300,
                                            }}>
                                            <Controller
                                              control={methods.control}
                                              name={`reportParameterColl.${index}.unitValue`}
                                              render={({ field }) => (
                                                <TextField
                                                  {...field}
                                                  placeholder="000.0"
                                                  fullWidth
                                                  id="unitValue"
                                                  variant="standard"
                                                  InputProps={{
                                                    disableUnderline: true,
                                                  }}
                                                  sx={{
                                                    paddingRight: "5px",
                                                    borderRight:
                                                      "1px solid #e0e0e0",
                                                    "& .MuiInputBase-input": {
                                                      textAlign: "right",
                                                      fontSize: "18px",
                                                      fontWeight: 600,
                                                    },
                                                    "& .MuiInputBase-input::placeholder":
                                                    {
                                                      fontSize: "16px",
                                                      fontWeight: 400,
                                                    },
                                                  }}
                                                />
                                              )}
                                            />
                                            <Controller
                                              control={methods.control}
                                              name={`reportParameterColl.${index}.unitName`}
                                              render={({ field }) => {
                                                const unitOptions = unitColl
                                                  .filter(
                                                    (x: any) =>
                                                      x?.investigationParameterId ===
                                                      item?.investigationParameterId,
                                                  )
                                                  .flatMap((unit: any) => {
                                                    const opts = [
                                                      {
                                                        key: `${unit.investigationParameterUnitId}-primary`,
                                                        label: unit.unitName,
                                                        investigationParameterUnitId:
                                                          unit.investigationParameterUnitId,
                                                      },
                                                    ];
                                                    if (
                                                      unit.optionalUnit &&
                                                      unit.optionalUnit.trim() !==
                                                      ""
                                                    ) {
                                                      opts.push({
                                                        key: `${unit.investigationParameterUnitId}-optional`,
                                                        label: unit.optionalUnit,
                                                        investigationParameterUnitId:
                                                          unit.investigationParameterUnitId,
                                                      });
                                                    }
                                                    return opts;
                                                  });

                                                const defaultUnit = unitColl.find(
                                                  (x: any) =>
                                                    x.investigationParameterId ===
                                                    item.investigationParameterId,
                                                );

                                                return (
                                                  <Select
                                                    {...field}
                                                    value={
                                                      field.value ||
                                                      defaultUnit?.unitName ||
                                                      ""
                                                    }
                                                    onChange={(e) => {
                                                      const selectedLabel =
                                                        e.target.value;
                                                      const selectedOption =
                                                        unitOptions.find(
                                                          (o) =>
                                                            o.label ===
                                                            selectedLabel,
                                                        );
                                                      methods.setValue(
                                                        `reportParameterColl.${index}.unitName`,
                                                        selectedLabel,
                                                      );
                                                      methods.setValue(
                                                        `reportParameterColl.${index}.investigationParameterUnitId`,
                                                        selectedOption?.investigationParameterUnitId ||
                                                        0,
                                                      );
                                                    }}
                                                    variant="standard"
                                                    size="small"
                                                    disableUnderline
                                                    MenuProps={{
                                                      PaperProps: {
                                                        sx: {
                                                          border:
                                                            "1px solid #e0e0e0",
                                                          borderRadius: 1,
                                                        },
                                                      },
                                                    }}
                                                    sx={{
                                                      px: 1,
                                                      borderColor: "divider",
                                                      minWidth: 120,
                                                    }}>
                                                    {unitOptions.map((opt) => (
                                                      <MenuItem
                                                        key={opt.key}
                                                        value={opt.label}>
                                                        {opt.label}
                                                      </MenuItem>
                                                    ))}
                                                  </Select>
                                                );
                                              }}
                                            />
                                          </Box>
                                        );
                                      })()}
                                    </Grid>
                                    <Grid item xs={12} sm={1.75} md={1.75}>
                                      <Typography
                                        sx={{
                                          display: { xs: "block", sm: "none" },
                                          fontWeight: 600,
                                        }}
                                      >
                                        Range
                                      </Typography>
                                      {item?.parameterType === "Qualitative" ? (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                                          {methods.watch(`reportParameterColl.${index}.rangeValue`) || item.rangeValue ? (
                                            <Box
                                              className="quick-chip"
                                              sx={{
                                                padding: "6px 10px !important",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "4px",
                                              }}>
                                              <span>{methods.watch(`reportParameterColl.${index}.rangeValue`) || item.rangeValue}</span>
                                              <Tooltip title="Edit Range" placement="top">
                                                <Button
                                                  sx={{ 
                                                    minWidth: "20px",
                                                    width: "20px",
                                                    height: "20px",
                                                    padding: 0,
                                                    marginTop: "-3px",
                                                    marginLeft: "2px",
                                                  }}
                                                  onClick={() => {
                                                    const opts = investigationParameterRangeColl
                                                      .filter((r: any) => r?.investigationParameterId === item?.investigationParameterId && r?.optionValue?.toString().trim() !== "")
                                                      .map((r: any) => ({ optionValue: r.optionValue }));
                                                    const currentRangeValue = methods.getValues(`reportParameterColl.${index}.rangeValue`) || item.rangeValue || "";
                                                    setQuickRangeSelected((prev) => ({ ...prev, [item.investigationParameterId]: currentRangeValue }));
                                                    setQuickRangeRow({ ...item, optionValue: JSON.stringify(opts) });
                                                    setOpenQuickRangeDialog(true);
                                                  }}>
                                                  <Edit 
                                                      fontSize="inherit"
                                                    sx={{ 
                                                      width: "14px !important",
                                                      height: "14px !important",
                                                    }} 
                                                  />
                                                </Button>
                                              </Tooltip>
                                            </Box>
                                          ) : (
                                            <Tooltip title="Add Range" placement="top">
                                              <IconButton
                                                size="small"
                                                onClick={() => {
                                                  const opts = investigationParameterRangeColl
                                                    .filter((r: any) => r?.investigationParameterId === item?.investigationParameterId && r?.optionValue?.toString().trim() !== "")
                                                    .map((r: any) => ({ optionValue: r.optionValue }));
                                                  setQuickRangeRow({ ...item, optionValue: JSON.stringify(opts) });
                                                  setOpenQuickRangeDialog(true);
                                                }}>
                                                <AddOutlinedIcon fontSize="small" className="text-link link-cursor" />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </Box>
                                      ) : (
                                        methods.watch(`reportParameterColl.${index}.rangeValue`) || item.rangeValue
                                      )}
                                    </Grid>
                                    <Grid item xs={12} sm={1.75} md={1.75}>
                                      <>
                                        <Typography
                                          sx={{
                                            display: {
                                              xs: "block",
                                              sm: "none",
                                            },
                                            fontWeight: 600,
                                          }}
                                        >
                                          Remarks
                                        </Typography>
                                        <Controller
                                          control={methods.control}
                                          name={`reportParameterColl.${index}.remarks`}
                                          render={({ field }) => (
                                            <CustomTextField
                                              {...field}
                                              placeholder="Remarks"
                                              fullWidth
                                              size="small"
                                            />
                                          )}
                                        />
                                      </>
                                    </Grid>
                                  </Grid>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {filteredReportParameterRows.length <= 0 && (
                            <TableRow>
                              <TableCell align="center" colSpan={6}>
                                No record(s) found
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} mt={1}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <LoadingButton
                    color="primary"
                    variant="contained"
                    type="button"
                    className="primary-button"
                    loading={isLoading}
                    onClick={handleOpenAddPopup}
                  >
                    Add Parameter
                  </LoadingButton>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="contained"
                      type="submit"
                      className="primary-button"
                      name="save"
                      loading={isLoading}
                    >
                      Save
                    </LoadingButton>
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="outlined"
                      type="button"
                      className="secondary-button"
                      name="cancel"
                      onClick={handleBack}
                      disabled={isLoading}
                    >
                      Cancel
                    </LoadingButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>

      {/* DIALOG FOR ADD PARAMETER */}
      <Dialog
        open={openParameterDialog}
        onClose={() => setOpenParameterDialog(false)}
        fullScreen
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <AppBar
          sx={{
            position: "relative",
            backgroundColor: "#f8fafc",
          }}
        >
          <Toolbar>
            <Typography
              style={{ fontWeight: 600, marginLeft: 0 }}
              sx={{ ml: 2, flex: 1 }}
              variant="h6"
            >
              Add Parameter
            </Typography>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <Grid>
          <SkeletonWrapper
            isLoading={isLoading}
            className="skeleton-search-box"
          >
            <TextField
              id="searchBox"
              placeholder="Search..."
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleDialogSearch(dialogValue);
                }
              }}
              type="text"
              variant="outlined"
              size="small"
              onChange={(e) => setDialogValue(e.target.value)}
              value={dialogValue}
              sx={{
                padding: "20px 20px 0px 20px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleDialogSearch(dialogValue);
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: dialogValue && (
                  <IconButton
                    aria-label="clear search"
                    onClick={() => {
                      setDialogValue("");
                      handleDialogSearch("");
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
          </SkeletonWrapper>
        </Grid>
        <DialogContent
          sx={{
            pb: 0,
            pl: 0,
            pr: 0,
          }}
        >
          {reportParameterList?.length > 0 ? (
            <>
              <TableContainer
                component={Paper}
                elevation={0} sx={{ display: { xs: "none", sm: "block" } }}>
                <Table size="small">
                  <TableHead
                    sx={{
                      "& .MuiTableCell-head": {
                        padding: "8px !important",
                      },
                    }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: "5%",
                        }}>
                        <Checkbox
                          checked={
                            reportParameterList?.length > 0 &&
                            reportParameterList.every((row: any) =>
                              dialogSelected?.includes(
                                row.investigationParameterId,
                              ),
                            )
                          }
                          indeterminate={
                            reportParameterList?.some((row: any) =>
                              dialogSelected?.includes(
                                row.investigationParameterId,
                              ),
                            ) &&
                            !reportParameterList?.every((row: any) =>
                              dialogSelected?.includes(
                                row.investigationParameterId,
                              ),
                            )
                          }
                          onChange={handleCheckSelectAll}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          display: {
                            xs: "none",
                            sm: "table-cell",
                          },
                        }}
                        align="left">
                        Parameter
                      </TableCell>
                      <TableCell
                        sx={{
                          display: {
                            xs: "none",
                            sm: "table-cell",
                          },
                          fontWeight: "bold",
                          width: "10%",
                        }}
                        align="left">
                        Specimen
                      </TableCell>
                      <TableCell
                        sx={{
                          display: {
                            xs: "none",
                            sm: "table-cell",
                          },
                          fontWeight: "bold",
                          width: "10%",
                        }}
                        align="left">
                        Unit
                      </TableCell>
                      <TableCell
                        sx={{
                          display: {
                            xs: "none",
                            sm: "table-cell",
                          },
                          fontWeight: "bold",
                          width: "10%",
                        }}
                        align="left">
                        Parameter Type
                      </TableCell>
                      <TableCell
                        sx={{
                          display: {
                            xs: "none",
                            sm: "table-cell",
                          },
                          fontWeight: "bold",
                          width: "30%",
                        }}
                        align="left">
                        Value
                      </TableCell>
                      <TableCell
                        sx={{
                          display: {
                            xs: "none",
                            sm: "table-cell",
                          },
                          fontWeight: "bold",
                          width: "20%",
                        }}
                        align="left">
                        Range
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportParameterList.map((row: any) => {
                      return (
                        <TableRow key={row.investigationParameterId}>
                          <TableCell sx={{ width: "5%" }}>
                            <Checkbox
                              checked={dialogSelected?.includes(
                                row.investigationParameterId,
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setDialogSelected([
                                    ...dialogSelected,
                                    row.investigationParameterId,
                                  ]);
                                } else {
                                  setDialogSelected(
                                    dialogSelected.filter(
                                      (id) => id !== row.investigationParameterId,
                                    ),
                                  );
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {row.parameterName}
                          </TableCell>
                          <TableCell>
                            {row.specimen}
                          </TableCell>
                          <TableCell>
                            {row.unitName}
                          </TableCell>
                          <TableCell>
                            {row.parameterType}
                          </TableCell>
                          <TableCell>
                            {row?.parameterType === "Qualitative" && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}>
                                {getOptionValues(row?.optionValue)?.map(
                                  (item: any, index: number) => (
                                    <Box
                                      key={
                                        item?.memberPatientReportParameterForRangeValueId ??
                                        `${row.investigationParameterId}-${index}`
                                      }
                                      className="quick-chip"
                                      sx={{
                                        padding: "6px 10px !important",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        lineHeight: 1,
                                      }}
                                      onClick={() => {
                                        setSelectedRangeRow({
                                          ...row,
                                          optionValue: item?.optionValue || "",
                                          memberPatientReportParameterForRangeValueId:
                                            item?.memberPatientReportParameterForRangeValueId ||
                                            0,
                                        });
                                        setOpenRangeDialog(true);
                                      }}>
                                      <span>{item?.optionValue}</span>
                                      <Tooltip
                                        title="Edit Option Value"
                                        placement="top">
                                        <Button
                                          sx={{
                                            minWidth: "20px",
                                            width: "20px",
                                            height: "20px",
                                            padding: 0,
                                            marginTop: "-3px",
                                            marginLeft: "2px",
                                          }}>
                                          <Edit
                                            fontSize="inherit"
                                            sx={{
                                              width: "14px !important",
                                              height: "14px !important",
                                            }}
                                          />
                                        </Button>
                                      </Tooltip>
                                    </Box>
                                  ),
                                )}
                                <Tooltip title="Add Option Value" placement="top">
                                  <IconButton size="small">
                                    <AddOutlinedIcon
                                      fontSize="small"
                                      className="text-link link-cursor"
                                      onClick={() => {
                                        setSelectedRangeRow({
                                          ...row,
                                          optionValue: "",
                                          memberPatientReportParameterForRangeValueId: 0,
                                        });
                                        setOpenRangeDialog(true);
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            {row?.parameterType === "Qualitative" ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  flexWrap: "wrap",
                                }}>
                                {quickRangeSelected[
                                  row.investigationParameterId
                                ] ? (
                                  <Box
                                    className="quick-chip"
                                    sx={{
                                      padding: "6px 10px !important",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      lineHeight: 1,
                                    }}>
                                    <span>
                                      {
                                        quickRangeSelected[
                                        row.investigationParameterId
                                        ]
                                      }
                                    </span>
                                    <Tooltip
                                      title="Edit Option Value"
                                      placement="top">
                                      <Button
                                        sx={{
                                          minWidth: "20px",
                                          width: "20px",
                                          height: "20px",
                                          padding: 0,
                                          marginTop: "-3px",
                                          marginLeft: "2px",
                                        }}
                                        onClick={() => {
                                          setQuickRangeRow(row);
                                          setOpenQuickRangeDialog(true);
                                        }}>
                                        <Edit
                                          fontSize="inherit"
                                          sx={{
                                            width: "14px !important",
                                            height: "14px !important",
                                          }}
                                        />
                                      </Button>
                                    </Tooltip>
                                  </Box>
                                ) : (
                                  <IconButton size="small">
                                    <AddOutlinedIcon
                                      fontSize="small"
                                      className="text-link link-cursor"
                                      onClick={() => {
                                        setQuickRangeRow(row);
                                        setOpenQuickRangeDialog(true);
                                      }}
                                    />
                                  </IconButton>
                                )}
                              </Box>
                            ) : (
                              row.rangeValue
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: { xs: "block", sm: "none" }, px: 2 }}>
                {reportParameterList.map((row: any, idx: number) => {
                  return (
                    <Box
                      sx={{
                        borderBottom: idx === reportParameterList.length - 1 ? "none" : "1px solid #e2e8f0",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography>
                          <span style={{ fontWeight: "bold" }}>
                            Parameter:
                          </span>{" "}
                          {row.parameterName || ""}
                        </Typography>
                        <Checkbox
                          checked={dialogSelected?.includes(
                            row.investigationParameterId,
                          )}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setDialogSelected([
                                ...dialogSelected,
                                row.investigationParameterId,
                              ]);
                            } else {
                              setDialogSelected(
                                dialogSelected.filter(
                                  (id) => id !== row.investigationParameterId,
                                ),
                              );
                            }
                          }}
                        />
                      </Box>
                      <Typography>
                        <span style={{ fontWeight: "bold" }}>
                          Specimen:
                        </span>{" "}
                        {row.specimen || "-"}
                      </Typography>
                      <Typography>
                        <span style={{ fontWeight: "bold" }}>
                          Unit:
                        </span>{" "}
                        {row.unitName || "-"}
                      </Typography>
                      <Typography>
                        <span style={{ fontWeight: "bold" }}>
                          Parameter Type:
                        </span>{" "}
                        {row.parameterType || "-"}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                        <span style={{ fontWeight: "bold" }}>
                          Value:
                        </span>
                        {row?.parameterType === "Qualitative" && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}>
                            {getOptionValues(row?.optionValue)?.map(
                              (item: any, index: number) => (
                                <Box
                                  key={
                                    item?.memberPatientReportParameterForRangeValueId ??
                                    `${row.investigationParameterId}-${index}`
                                  }
                                  sx={{
                                    border: "1px solid rgb(198 220 235)",
                                    borderRadius: "8px",
                                    padding: "3px 5px !important",
                                    height: "22px !important",
                                    minHeight: "22px",
                                    color: "#222",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "2px",
                                  }}
                                  onClick={() => {
                                    setSelectedRangeRow({
                                      ...row,
                                      optionValue: item?.optionValue || "",
                                      memberPatientReportParameterForRangeValueId:
                                        item?.memberPatientReportParameterForRangeValueId ||
                                        0,
                                    });
                                    setOpenRangeDialog(true);
                                  }}>
                                  <span>{item?.optionValue}</span>
                                  <Tooltip
                                    title="Edit Option Value"
                                    placement="top">
                                    <Button
                                      sx={{
                                        minWidth: "20px",
                                        width: "20px",
                                        height: "20px",
                                        padding: 0,
                                        marginTop: "-3px",
                                        marginLeft: "2px",
                                      }}>
                                      <Edit
                                        fontSize="inherit"
                                        sx={{
                                          width: "14px !important",
                                          height: "14px !important",
                                        }}
                                      />
                                    </Button>
                                  </Tooltip>
                                </Box>
                              ),
                            )}
                            <Tooltip title="Add Option Value" placement="top">
                              <IconButton
                                size="small"
                                sx={{
                                  padding: 0,
                                }}
                                onClick={() => {
                                  setSelectedRangeRow({
                                    ...row,
                                    optionValue: "",
                                    memberPatientReportParameterForRangeValueId: 0,
                                  });
                                  setOpenRangeDialog(true);
                                }}>
                                <AddOutlinedIcon
                                  sx={{
                                    fontSize: "18px !important",
                                    color: "primary.main",
                                    cursor: "pointer",
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5, mb: 1 }}>
                        <span style={{ fontWeight: "bold" }}>
                          Range:
                        </span>
                        {row?.parameterType === "Qualitative" ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              flexWrap: "wrap",
                            }}>
                            {quickRangeSelected[
                              row.investigationParameterId
                            ] ? (
                              <Box
                                sx={{
                                  border: "1px solid rgb(198 220 235)",
                                  borderRadius: "8px",
                                  padding: "3px 5px !important",
                                  height: "22px !important",
                                  minHeight: "22px",
                                  color: "#222",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}>
                                <span>
                                  {
                                    quickRangeSelected[
                                    row.investigationParameterId
                                    ]
                                  }
                                </span>
                                <Tooltip
                                  title="Edit Option Value"
                                  placement="top">
                                  <Button
                                    sx={{
                                      minWidth: "20px",
                                      width: "20px",
                                      height: "20px",
                                      padding: 0,
                                      marginTop: "-3px",
                                      marginLeft: "2px",
                                    }}
                                    onClick={() => {
                                      setQuickRangeRow(row);
                                      setOpenQuickRangeDialog(true);
                                    }}>
                                    <Edit
                                      fontSize="inherit"
                                      sx={{
                                        width: "14px !important",
                                        height: "14px !important",
                                      }}
                                    />
                                  </Button>
                                </Tooltip>
                              </Box>
                            ) : (
                              <IconButton
                                size="small"
                                sx={{
                                  padding: 0,
                                }}
                                onClick={() => {
                                  setQuickRangeRow(row);
                                  setOpenQuickRangeDialog(true);
                                }}
                              >
                                <AddOutlinedIcon
                                  sx={{
                                    fontSize: "18px !important",
                                    color: "primary.main",
                                    cursor: "pointer",
                                  }}
                                />
                              </IconButton>
                            )}
                          </Box>
                        ) : (
                          row.rangeValue
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            </>
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No record(s) found</Typography>
            </Box>
          )}
        </DialogContent>
        {/* PAGINATION */}
        {totalCount > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="body2">
              {pageStartIndex} - {pageEndIndex} of {totalCount}
            </Typography>
            <Stack>
              <Pagination
                color="primary"
                count={counData}
                page={dialogPage}
                onChange={handleDialogPageChange}
              />
            </Stack>
          </Box>
        )}
        <DialogActions>
          <Button
            color="primary"
            variant="outlined"
            className="secondary-button"
            onClick={() => setOpenParameterDialog(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            className="primary-button"
            onClick={handleAddSelectedParameters}
          >
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        className="dialogPage"
        open={openRangeDialog}
        onClose={() => setOpenRangeDialog(false)}
        fullScreen
        PaperProps={{
          style: {
            width: "500px",
            height: "250px",
            maxWidth: "none",
            borderRadius: "10px",
          },
        }}>
        <AppBar
          sx={{
            position: "relative",
            backgroundColor: "#f8fafc",
          }}>
          <Toolbar>
            <Typography
              style={{ fontWeight: 600, marginLeft: 0 }}
              sx={{ ml: 2, flex: 1 }}
              variant="h6">
              Value Option
            </Typography>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <DialogContent sx={{ padding: "12px 24px" }}>
          <ReportParameterForRange
            handleClosePopUp={(code: any) => {
              handleManagePopup(code);
            }}
            isForPopUp={true}
            name={customPopUp.name}
            opd={opd}
            code="RANGE_VALUE"
            investigationId={selectedRangeRow?.investigationId}
            investigationParameterId={
              selectedRangeRow?.investigationParameterId
            }
            optionValue={selectedRangeRow?.optionValue || ""}
            memberPatientReportParameterForRangeValueId={
              selectedRangeRow?.memberPatientReportParameterForRangeValueId || 0
            }
          />
        </DialogContent>
      </Dialog>
      <Dialog
        className="dialogPage"
        open={openQuickRangeDialog}
        onClose={() => setOpenQuickRangeDialog(false)}
        fullScreen
        PaperProps={{
          style: {
            width: "500px",
            height: "250px",
            maxWidth: "none",
            borderRadius: "10px",
          },
        }}>
        <AppBar
          sx={{
            position: "relative",
            backgroundColor: "#f8fafc",
          }}>
          <Toolbar>
            <Typography
              style={{ fontWeight: 600, marginLeft: 0 }}
              sx={{ ml: 2, flex: 1 }}
              variant="h6">
              Range
            </Typography>
            <IconButton
              onClick={() => setOpenQuickRangeDialog(false)}
              aria-label="close"
              sx={{ color: "#555" }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <DialogContent sx={{ padding: "12px 24px" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {getOptionValues(quickRangeRow?.optionValue)?.map(
              (item: any, index: number) => {
                const isSelected =
                  quickRangeRow &&
                  quickRangeSelected[quickRangeRow.investigationParameterId] ===
                  item?.optionValue;
                return (
                  <Box
                    key={
                      item?.memberPatientReportParameterForRangeValueId ??
                      `${quickRangeRow?.investigationParameterId}-${index}`
                    }
                    className="quick-chip"
                    sx={{
                      padding: "6px 10px !important",
                      cursor: "pointer",
                      ...(isSelected && {
                        "&&": {
                          backgroundColor: "primary.main",
                          color: "#ffff",
                          borderColor: "primary.main",
                        },
                      }),
                    }}
                    onClick={() => {
                      if (quickRangeRow) {
                        const newOptionValue = item?.optionValue || "";
                        setQuickRangeSelected((prev) => ({
                          ...prev,
                          [quickRangeRow.investigationParameterId]:
                            newOptionValue,
                        }));
                        const existingIndex = reportParameterData?.findIndex(
                          (p: any) =>
                            p?.investigationParameterId ===
                            quickRangeRow.investigationParameterId,
                        );
                        if (existingIndex !== undefined && existingIndex >= 0) {
                          const updatedData = [...reportParameterData];
                          updatedData[existingIndex] = {
                            ...updatedData[existingIndex],
                            rangeValue: newOptionValue,
                          };
                          setReportParameterData(updatedData);
                          methods.setValue(
                            `reportParameterColl.${existingIndex}.rangeValue`,
                            newOptionValue,
                            { shouldDirty: true, shouldValidate: true },
                          );
                        }
                      }
                      setOpenQuickRangeDialog(false);
                    }}>
                    {item?.optionValue}
                  </Box>
                );
              },
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatientReportParameter;
